<template>
    <v-container fluid class="pa-0">
        <CalculationTraderMarginDialog
            :is-open="calculationTraderMarginDialog"
            :calculation-trader-margin-data="calculationTraderMargin"
            :action-loading="actionLoading.saveCalculationTraderMargin"
            @on-close="closeCalculationTraderMarginDialog"
            @on-save="updateCalculationTraderMargin"
        />
        <CalculationPriceMaterialDialog
            :products="products"
            :calculation-id="calculation.id"
            :is-calculation-editable="isCalculationEditable"
            :is-open="calculationPriceMaterialDialog"
            @on-close="closeCalculationPriceMaterialDialog"
        />
        <CalculationConfirmRemoveEmptyImpositionsDialog
            :is-open="calculationConfirmRemoveEmptyImpositionsDialog"
            :calculation-confirm-remove-empty-impositions-data="calculation.calculationImpositions"
            :action-loading="actionLoading.saveCalculationConfirmRemoveEmptyImpositions"
            @on-close="closeCalculationConfirmRemoveEmptyImpositionsDialog"
            @on-save="saveCalculation"
        />
        <v-row>
            <v-col cols="12">
                <v-container fluid class="pa-0">
                    <v-row>
                        <v-col v-if="customer" cols="12" md="6" lg="4">
                            <h2 class="mb-3 text-body-2 font-weight-medium text-uppercase">
                                Klient
                            </h2>

                            {{ customer.name }} <br>

                            <template v-if="customer.address">
                                {{ customer.address.street }} <br>
                                {{ customer.address.postCode }} {{ customer.address.city }} <br>
                            </template>
                            NIP: <strong>{{ customer.nip }}</strong>
                        </v-col>

                        <v-col v-if="calculationTraderMargin" cols="12" md="6" lg="8">
                            <h2 class="mb-3 text-body-2 font-weight-medium text-uppercase">
                                Wycena
                            </h2>
                            cena bazowa: <strong>{{ formatPrice(calculation.priceBeforeTraderMargin) }} zł</strong> <br>
                            narzut handlowca: <strong>{{ calculationTraderMargin.value }}%</strong>

                            <v-btn
                                color="primary"
                                class="ml-2"
                                x-small
                                depressed
                                @click="showCalculationTraderMarginDialog"
                            >
                                Zmień
                            </v-btn>
                            <br>
                            narzut (zł): <strong>{{ formatPrice(calculation.priceBeforeTraderMargin * calculationTraderMargin.value / 100) }} zł</strong> <br>
                            cena ofertowa: <strong>{{ formatPrice(calculation.netPrice) }} zł</strong> <br>
                            <div class="justify mt-3">
                                <v-btn
                                    color="primary"
                                    class="mr-2"
                                    small
                                    depressed
                                    @click="showCalculationPriceMaterialDialog"
                                >
                                    Edycja cen
                                </v-btn>
                                <v-btn
                                    color="primary"
                                    outlined
                                    small
                                    depressed
                                    :loading="actionLoading.printCalculationReport"
                                    @click="printCalculationReport(calculation)"
                                >
                                    <v-icon
                                        left
                                    >
                                        mdi-printer
                                    </v-icon>
                                    Generuj raport
                                </v-btn>
                            </div>
                        </v-col>
                    </v-row>
                </v-container>

                <v-divider class="mt-7 mb-6" />

                <v-container fluid class="pa-0 mt-3">
                    <v-row>
                        <v-col cols="12">
                            <h2 class="mb-3 text-body-2 font-weight-medium text-uppercase">
                                Produkty
                            </h2>

                            <v-data-table
                                :headers="productHeaders"
                                :items="products"
                                disable-pagination
                                hide-default-footer
                                :mobile-breakpoint="0"
                            >
                                <template #item.productType="{item}">
                                    {{ item.productType.verboseName }}
                                </template>
                                <template #item.productName="{item}">
                                    {{ item.name }}
                                </template>
                                <template #item.amount="{item}">
                                    {{ item.amount }}
                                </template>
                            </v-data-table>
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>

        <v-row no-gutters justify="space-between">
            <v-col cols="auto">
                <v-btn
                    color="primary"
                    outlined
                    class="mt-3 mb-2"
                    data-sentry-label="Poprzedni krok"
                    :disabled="actionLoading.updateCalculationTraderMargin"
                    @click="showCalculationImpositions"
                >
                    Poprzedni krok
                </v-btn>
            </v-col>
            <v-col cols="auto">
                <v-btn
                    v-if="isCalculationEditable"
                    color="success"
                    depressed
                    class="mt-3 mb-2"
                    :loading="actionLoading.saveCalculation"
                    :disabled="actionLoading.updateCalculationTraderMargin"
                    @click="confirmCalculation"
                >
                    Zatwierdź
                </v-btn>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import CalculationTraderMarginDialog from './CalculationTraderMarginDialog.vue'
import CalculationPriceMaterialDialog from './CalculationPriceMaterialDialog.vue'
import CalculationConfirmRemoveEmptyImpositionsDialog from './CalculationConfirmRemoveEmptyImpositionsDialog.vue'
import priceFormatter from '@/mixins/priceFormatter'

import { openPdfFile } from '@/utils/pdfHandler'
import { errorHandler, addSentryBreadcrumb } from '@/utils/errorHandler'

import { mask } from 'vue-the-mask'

export default {
    name: 'CalculationSummary',
    components: {
        CalculationTraderMarginDialog,
        CalculationPriceMaterialDialog,
        CalculationConfirmRemoveEmptyImpositionsDialog
    },
    directives: { mask },
    mixins: [ priceFormatter ],
    props: {
        isVisible: {
            type: Boolean
        },
        isCalculationEditable: {
            type: Boolean
        },
        isCalculationInvalid: {
            type: Boolean
        }
    },
    data() {
        return {
            emptyImpositions: [],
            calculationTraderMarginDialog: false,
            calculationPriceMaterialDialog:false,
            calculationConfirmRemoveEmptyImpositionsDialog: false,
            actionLoading: {
                saveCalculation: false,
                saveCalculationTraderMargin: false,
                saveCalculationConfirmRemoveEmptyImpositions: false,
                printCalculationReport: false,
            },
            productHeaders: [
                {
                    text: 'Rodzaj produktu',
                    value: 'productType',
                    sortable: false,
                },
                {
                    text: 'Nazwa produktu',
                    value: 'productName',
                    sortable: false,
                },
                {
                    text: 'Nakład',
                    value: 'amount',
                    align: 'center',
                    sortable: false,
                    width: 140
                }
            ],
        }
    },
    computed: {
        calculation() {
            return this.$store.state.calculations.calculations.calculation
        },
        customer() {
            if (this.calculation.customer) {
                return {
                    name: this.calculation.customer.name,
                    address: this.calculation.customer.customerAddresses.find(item => item.addressTypeId === 1), // Adres kontaktowy
                    nip: this.calculation.customer.nip
                }
            } else {
                return null
            }
        },
        calculationTraderMargin() {
            return this.calculation.calculationTraderMargin
        },
        products() {
            return this.calculation.calculationProducts.filter(product => {
                return product.calculationProductComponents.some(item => item.calculationImpositionId !== null)
            })
        }
    },
    methods: {
        showCalculationTraderMarginDialog() {
            this.calculationTraderMarginDialog = true
        },
        closeCalculationTraderMarginDialog() {
            this.calculationTraderMarginDialog = false
        },
        showCalculationPriceMaterialDialog() {
            this.calculationPriceMaterialDialog = true
        },
        closeCalculationPriceMaterialDialog() {
            this.calculationPriceMaterialDialog = false
        },
        showCalculationConfirmRemoveEmptyImpositionsDialog() {
            this.calculationConfirmRemoveEmptyImpositionsDialog = true
        },
        closeCalculationConfirmRemoveEmptyImpositionsDialog() {
            this.calculationConfirmRemoveEmptyImpositionsDialog = false
        },
        updateCalculationTraderMargin(payload) {
            addSentryBreadcrumb('ui.action', 'Aktualizacja narzutu handlowca')

            this.actionLoading.saveCalculationTraderMargin = true

            this.$store.dispatch('calculations/updateCalculationTraderMargin', payload)
                .then(() => {
                    this.$store.dispatch('calculations/getCalculation', this.calculation.id)
                        .catch(error => errorHandler(error))
                        .finally(() => {
                            this.closeCalculationTraderMarginDialog()
                            this.actionLoading.saveCalculationTraderMargin = false
                        })
                })
                .catch(error => {
                    this.closeCalculationTraderMarginDialog()
                    this.actionLoading.saveCalculationTraderMargin = false
                    errorHandler(error)
                })
        },
        printCalculationReport(calculation) {
            this.actionLoading.printCalculationReport = true

            this.$store.dispatch('calculations/printCalculationReport', {
                calculationId: calculation.id,
                templateCodes: ['calculation_report']
            })
                .then(response => {
                    openPdfFile(response.data)
                })
                .catch(error => errorHandler(error))
                .finally(() => {
                    this.actionLoading.printCalculationReport = false
                })
        },
        showCalculationImpositions() {
            this.$emit('show-calculation-impositions')
        },
        confirmCalculation() {
            this.emptyImpositions = []
            this.calculation.calculationImpositions.forEach(calculationImposition => {
                if (calculationImposition.calculationProductComponentIds.length == 0) {
                    this.emptyImpositions.push(calculationImposition)
                }
            })
            if (this.emptyImpositions.length !== 0) {
                this.calculationConfirmRemoveEmptyImpositionsDialog = true
            } else {
                this.saveCalculation()
            }
        },
        saveCalculation() {
            addSentryBreadcrumb('ui.action', 'Zatwierdzenie kalkulacji')

            this.calculationConfirmRemoveEmptyImpositionsDialog = false

            if (this.isCalculationInvalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: 'Proszę poprawnie rozpisać kalkulację.'
                })
            }
            else {
                this.actionLoading.saveCalculation = true

                this.$store.dispatch('calculations/updateCalculation', {
                    ...this.calculation,
                    statusId: 15 //Zatwierdzona
                })
                    .then(() => {
                        this.$store.dispatch('showNotification', {
                            type: 'success',
                            message: 'Zatwierdzono kalkulację.'
                        })
                    })
                    .catch(error => {
                        errorHandler(error)
                    })
                    .finally(() => {
                        this.actionLoading.saveCalculation = false
                    })
            }
        }
    }
}
</script>